import React from 'react';
import { ChevronDown } from 'lucide-react';
import {
  type ChecklistClearAll,
  type ChecklistFooter,
  type ChecklistOption,
  ChecklistPopover,
  useChecklist,
} from '../checklist';
import { isOnOpenIcon, openIconProps } from '../dom';

export type ChecklistCellProps = {
  options: readonly ChecklistOption[];
  value: readonly string[];
  onChange: (value: string[]) => void;
  /** Shown when nothing is picked. */
  placeholder?: string;
  ariaLabel: string;
  disabled?: boolean;
  /** One value at a time; picking closes the list. */
  single?: boolean;
  /** Shown in the cell instead of the picked labels. */
  text?: string;
  /** A last row in the list that closes it and runs an action. */
  footer?: ChecklistFooter;
  /** "Clear all" with its own action, instead of unticking what is shown. */
  clearAll?: ChecklistClearAll;
};

/**
 * A checklist drawn as a grid cell: no border or button chrome of its own,
 * the grid's cell is the box. As with the other cells, a single click only
 * selects the cell; a double click, Enter or Space (a click from code), or the
 * chevron opens the list. Tab in the open list closes it and moves the grid's
 * cursor on.
 */
export const ChecklistCell: React.FC<ChecklistCellProps> = ({
  options,
  value,
  onChange,
  placeholder,
  ariaLabel,
  disabled,
  single = false,
  text,
  footer,
  clearAll,
}) => {
  const checklist = useChecklist({
    options,
    value,
    onChange,
    single,
    gridCell: true,
    disabled,
    footer,
    clearAll,
  });
  // Labels in the options' order. A saved value with no option (an archived
  // entry, say) shows as itself after them, rather than vanishing.
  const labels = [
    ...options
      .filter(option => value.includes(option.value))
      .map(option => option.label),
    ...value.filter(v => !options.some(option => option.value === v)),
  ];
  const display = text ?? labels.join(', ');
  return (
    <ChecklistPopover
      checklist={checklist}
      trigger={
        <button
          ref={checklist.triggerRef}
          type='button'
          role='combobox'
          aria-expanded={checklist.open}
          // The picked values are part of the name: a screen reader moving
          // through the grid hears what the cell holds, not only what it is.
          aria-label={display ? `${ariaLabel}: ${display}` : ariaLabel}
          disabled={disabled}
          onClick={e => {
            // Radix skips its own toggle when the click is prevented: a
            // pointer click away from the chevron only selects the cell.
            if (e.detail !== 0 && !isOnOpenIcon(e.target)) e.preventDefault();
          }}
          onDoubleClick={checklist.openList}
          className='group flex h-9 w-full items-center gap-2 px-2 text-left text-[13px] hover:bg-muted/60 focus-visible:outline-none disabled:cursor-default disabled:opacity-60'
        >
          {value.length === 0 && !text ? (
            <span className='min-w-0 flex-1 truncate text-muted-foreground'>
              {placeholder}
            </span>
          ) : (
            <span className='min-w-0 flex-1 truncate'>{display}</span>
          )}
          <span
            {...openIconProps}
            className='-my-1 -mr-1 flex shrink-0 cursor-pointer items-center rounded p-1 hover:bg-muted'
          >
            <ChevronDown className='h-3 w-3 text-muted-foreground/50 group-hover:text-muted-foreground' />
          </span>
        </button>
      }
      contentClassName='w-auto min-w-[var(--radix-popover-trigger-width)] max-w-xs rounded-sm p-0.5'
      sideOffset={1}
      ariaLabel={ariaLabel}
      variant='cell'
    />
  );
};
